<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-red-700 cursor-pointer"
        @click="handleClick"
      >
        Cancel
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    Confirm your cancellation
  </DialogHeader>
  <DialogContent>
    <div v-if="hasOutsandingBalance">
      Are you sure you want to cancel your subscription and pay the outstanding
      balance of {{ outstandingBalance }}.
    </div>
    <div v-else>Are you sure you want to cancel your subscription?</div>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading"
      @click="close()"
    >
      Don't cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      danger
      :loading="loading"
      @click="handleClick"
    >
      Proceed and cancel
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import {
  BillingAlertType,
  BillingQueryDocument,
  PlanStatus,
  PlanType,
  UserPermission,
  useCancelSubscriptionMutation,
  type BillingQueryQuery,
  type User,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useUserStore } from '@/shared/stores/user'
import { formatPrice } from '@/shared/utils/money'
import { Icon, UiButton } from '@dacr/ui'
import gql from 'graphql-tag'
import { computed } from 'vue'

const { close, open } = useDialog()

const { mutate: cancelSubscription, loading } = useCancelSubscriptionMutation()

const billingStore = useBillingStore()
const userStore = useUserStore()
const toastStore = useToastStore()

const hasOutsandingBalance = computed(() =>
  billingStore.billing?.upcomingDetails.upcomingBilling?.breakdown.some(
    (breakdown) => breakdown.outstanding
  )
)

const outstandingBalance = computed(() => {
  const totalOutstanding =
    billingStore.billing?.upcomingDetails.upcomingBilling?.breakdown.reduce(
      (acc, breakdown) =>
        (acc += breakdown.outstanding ? breakdown.amount || 0 : 0),
      0
    ) || 0

  return formatPrice({
    amount: totalOutstanding / 100,
    currency: billingStore.billing?.upcomingDetails.upcomingBilling?.currency,
  })
})

const proceedWithCancelSubscription = async () => {
  await cancelSubscription(
    {},
    {
      update(cache) {
        if (!userStore.user) return

        cache.updateFragment<User>(
          {
            id: cache.identify(userStore.user),
            fragment: gql`
              fragment UpdateUserAccess on User {
                permissions
              }
            `,
          },
          (response) => {
            if (!response) return

            const newPermissions =
              billingStore.billing?.plan.type === PlanType.Pro
                ? userStore.user?.permissions.filter(
                    (permission) => permission !== UserPermission.Pro
                  )
                : userStore.user?.permissions

            return { permissions: newPermissions } as User
          }
        )

        cache.updateQuery<BillingQueryQuery>(
          { query: BillingQueryDocument },
          (response) => {
            if (!response) return

            return {
              ...response,
              billing: {
                ...response.billing,
                billingAlert: {
                  __typename: 'BillingAlert',
                  isActive: true,
                  type: BillingAlertType.WillCancel,
                },
                plan: {
                  ...response.billing.plan,
                  status: PlanStatus.Canceled,
                  dateInfo: {
                    __typename: 'BillingPlanDateInfo',
                    prefix: 'Canceled on',
                    date: Date.now(),
                  },
                },
              },
            }
          }
        )
      },
    }
  )
}

const handleClick = async () => {
  try {
    if (hasOutsandingBalance.value) {
      open(Dialogs.Pay, {
        events: {
          async success() {
            await proceedWithCancelSubscription()

            open(Dialogs.Success, {
              props: {
                title: 'Your subscription was cancelled',
                html: 'If you had a remaining balance that was paid you will receive an invoice once your payment has been processed.',
              },
              events: {
                success() {
                  close()
                },
              },
            })
          },
        },
      })
    } else {
      await proceedWithCancelSubscription()

      open(Dialogs.Success, {
        props: {
          title: 'Your subscription was cancelled',
          html: 'If you had a remaining balance that was paid you will receive an invoice once your payment has been processed.',
        },
        events: {
          success() {
            close()
          },
        },
      })
    }
  } catch (error) {
    console.error(error)

    toastStore.showToast({
      type: ToastType.Error,
      content: 'Could not cancel subscription. Please try again later.',
    })
  }
}
</script>
